import type { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Loader, Text, tokens } from '@hotjar/hotsauce';
import type { Collection } from '@hotjar/webapp/domains/collections/model';
import { useTranslation } from 'react-i18next';
import { CollectionsListItem } from './CollectionsListItem';
import type { CollectionItemAction, CollectionListItem } from './CollectionsListItem';

export type State = 'idle' | 'loading' | 'ready' | 'failed';

export interface ListProps {
  defaultAllHighlights: Collection;
  selectedCollectionId: string | undefined;
  className?: string;
  errorStateText?: string | ReactNode;
  emptyStateText?: string | ReactNode;
  items: CollectionListItem[];
  state: State;
  actions?: CollectionItemAction[];
  onItemSave?: (item: CollectionListItem) => void;
  onItemBlur?: () => void;
}

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const StyledLoader = styled(Loader)`
  width: ${tokens.spacer.x4};
  margin-left: -${tokens.spacer.x1};
`;

const StyledText = styled(Text)`
  display: block;
  margin-bottom: ${tokens.spacer.x2pt5};
  margin-left: ${tokens.spacer.x1};
`;

export const SidebarCollectionsList = ({
  selectedCollectionId,
  errorStateText,
  emptyStateText,
  items,
  className,
  state,
  defaultAllHighlights,
  actions,
  onItemBlur,
  onItemSave,
}: ListProps): JSX.Element => {
  const [t] = useTranslation();

  return (
    <div className={className}>
      {state === 'ready' && (
        <>
          {!items ? (
            <StyledText textType="caption" textColor="text_light">
              {emptyStateText}
            </StyledText>
          ) : (
            <StyledList aria-label={t('collections:sidebar.sidebar-title')}>
              <CollectionsListItem
                key={defaultAllHighlights.id}
                isActive={selectedCollectionId === defaultAllHighlights.id}
                item={defaultAllHighlights}
                isReadOnly={false}
                onItemBlur={onItemBlur}
              />
              {items.map((item) => (
                <CollectionsListItem
                  key={item.id}
                  isActive={selectedCollectionId === item.id}
                  item={item}
                  actions={actions}
                  isReadOnly={false}
                  onItemSave={onItemSave}
                  onItemBlur={onItemBlur}
                />
              ))}
            </StyledList>
          )}
        </>
      )}
      {state === 'loading' && (
        <StyledText textType="caption" textColor="text_light">
          <StyledLoader size="m" />
        </StyledText>
      )}
      {state === 'failed' && (
        <StyledText textType="caption" textColor="text_light">
          {errorStateText}
        </StyledText>
      )}
    </div>
  );
};
